import { z } from 'zod';
import {
  RANGEMENT_TEXT_MAX,
  rangementDiscardSchema,
  rangementObjectiveSchema,
} from '../rangement/output.js';
import { rangerSeatKeySchema } from './models.js';
import { noyauPromptFingerprintSchema } from './noyau-session.js';
import { turnCallSchema, turnPromptSchema } from './turn.js';

/**
 * What a SESSION records of its rangement (scenario-succes.md §3, §7, §9, issue
 * #177) - at the régime of the determination of the noyau
 * (`noyau-session.ts`): none of it can be rebuilt afterwards, because a
 * rangement is computed ONCE, stored, and never recomputed (§3.4).
 *
 * These are the shapes the lots build on. The tables that store them
 * (`session_rangements`, `session_rangement_trials`, migration 0018) belong to
 * the engine lot; the routes that serve them to the lot of the blank rangement.
 * None is `.strict()`, like every stored shape (§4.1), and every field added
 * later carries a read default (BUILD-787).
 *
 * ## The routes the blank rangement adds (§7.3, lot B)
 *
 * Written here, beside the shapes they serve, so the lot of Sessions can code
 * against the contract before the routes exist:
 *
 * - `POST /api/sessions/{id}/rangement/trials` - starts a trial in the
 *   background: 201 and the trial `pending` (`rangementTrialSchema`); a second
 *   press during a trial returns that one. 409 `rangementTrialErrorSchema`
 *   before any call.
 * - `GET /api/sessions/{id}/rangement/trials` - the trials of the session,
 *   newest first, without what the ranger was served (`rangementTrialListSchema`).
 * - `GET /api/sessions/{id}/rangement/trials/{trialId}` - one trial, entry
 *   served included; 404 when it is not the session's.
 * - `GET /api/sessions/{id}/rangement/prompts` - what the ranger of the session
 *   was served, call by call (`rangerPromptsSchema`), read on a click.
 */

/** The raw answer of the ranger, bounded where it is recorded - a defect meets it, never a model on purpose. */
export const RANGER_OUTPUT_MAX = 100_000;

/** One call and ONE retry, whatever the failure (§3.4) - plus room for a margin nobody should need. */
export const RANGER_CALLS_MAX = 4;

/** The bound of a recorded prompt version. */
export const RANGER_PROMPT_VERSION_MAX = 64;

/**
 * The fingerprint of the system a ranger's call was served: the first 8
 * hexadecimal characters of the SHA-256 of that system, in UTF-8 - the very
 * spelling of the analyst's (`noyauPromptFingerprintSchema`), computed by the
 * server. Two calls of one fingerprint received exactly the same instrument,
 * the consigne included: it is what the measure of §10 reads « une consigne »
 * on.
 */
export const rangerPromptFingerprintSchema = noyauPromptFingerprintSchema;
export type RangerPromptFingerprint = z.infer<typeof rangerPromptFingerprintSchema>;

/**
 * ONE call of the ranger, « comme un appel de tour »: the fields of
 * `turnCallSchema` with the seat narrowed, and the four that
 * `noyauAnalystCallSchema` adds for the same reasons - the raw `output`, whether
 * it was `malformed` (failing `rangerOutputSchema` on a call that itself
 * succeeded), the version of the assembly and the fingerprint of the system.
 */
export const rangerCallSchema = turnCallSchema.extend({
  seat: rangerSeatKeySchema,
  /** What the model answered, verbatim - the one attempt nobody can rebuild is the malformed one. */
  output: z.string().max(RANGER_OUTPUT_MAX).default(''),
  /** The call returned, but its answer did not read as a rangement. */
  malformed: z.boolean().default(false),
  /** `RANGER_PROMPT_VERSION` at the time of the call; null when none was recorded. */
  promptVersion: z.string().min(1).max(RANGER_PROMPT_VERSION_MAX).nullable().default(null),
  promptFingerprint: rangerPromptFingerprintSchema.nullable().default(null),
});
export type RangerCall = z.infer<typeof rangerCallSchema>;

/**
 * What the ranger was SENT on one call, exactly as it left the server - « l'entrée
 * servie », in its own table and read on a click (§9), at the régime of
 * `noyauAnalystPromptSchema`: it carries the person's words a second time, so it
 * is purged with the session.
 */
export const rangerPromptSchema = turnPromptSchema.extend({
  seat: rangerSeatKeySchema,
});
export type RangerPrompt = z.infer<typeof rangerPromptSchema>;

/** `GET /api/sessions/{id}/rangement/prompts` - the calls' entries, in the order of `calls`. */
export const rangerPromptsSchema = z.object({
  prompts: z.array(rangerPromptSchema).default([]),
});
export type RangerPrompts = z.infer<typeof rangerPromptsSchema>;

/**
 * The state of a session's rangement (§3.3, §3.4, §7.2): `pending` from its
 * launch, then one of three settled states - `rendered`, `failed` (two failed
 * calls: the determination read the scribe's matter), and `no-matter` (no reply
 * of the person: settled at once, no call).
 *
 * « Aucun » (§7.2) is NOT a state: a protocol with no target has no rangement,
 * and the session's `rangement` is null.
 */
export const rangementStateSchema = z.enum(['pending', 'rendered', 'failed', 'no-matter']);
export type RangementState = z.infer<typeof rangementStateSchema>;

/** The protocol version the consigne was taken from (§7.1: « version, lien vers la vue Rangement »). */
export const rangementProtocolVersionSchema = z.object({
  versionId: z.string().min(1),
  /** The number of the version, the one the screens print. */
  version: z.number().int().positive(),
});
export type RangementProtocolVersion = z.infer<typeof rangementProtocolVersionSchema>;

/**
 * A passage the code removed because the setting « Un passage ne sert qu'un
 * objectif » was ticked (relecture.md §12, D27): two extracts of different
 * targets overlapped in the same turn, the one of the FIRST objective in the
 * order of the protocol was kept (`objectiveId`) and the other removed
 * (`removedFrom`). Counted apart from the extracts the verbatim check removed.
 * `turn` and `text` are those of the extract that was REMOVED, which the
 * rangement no longer carries.
 */
export const rangementDuplicateExtractSchema = z.object({
  objectiveId: z.string().min(1),
  removedFrom: z.string().min(1),
  turn: z.number().int().positive(),
  text: z.string().min(1).max(RANGEMENT_TEXT_MAX),
});
export type RangementDuplicateExtract = z.infer<typeof rangementDuplicateExtractSchema>;

/** The list as a column stores it: `[]` by default, so a rangement of before reads none. */
export const rangementDuplicateExtractsSchema = z
  .array(rangementDuplicateExtractSchema)
  .default([]);

/**
 * What a rangement and a trial of it share: where the consigne came from, the
 * result, the calls and the clock.
 *
 * - `objectives`, `discarded` - the rangement AFTER `verifiedRangement`; null
 *   until `rendered`, and null on `failed` and `no-matter`.
 * - `removedExtracts` - how many passages the verbatim check removed (§3.2).
 * - `duplicateExtracts` - the passages removed because they served two targets
 *   under the setting « un seul objectif » (relecture.md §12); empty when the
 *   setting is unticked, and on a rangement written before it existed.
 * - `calls` - every call, the retry included: none on `no-matter`.
 * - `costEur` - the SUM of `calls` (`turnCostEur`).
 * - `startedAt`, `settledAt` - the instant of the gesture that led to the
 *   launch, and the settlement.
 */
const rangementFigureShape = {
  protocolVersion: rangementProtocolVersionSchema,
  promptFingerprint: rangerPromptFingerprintSchema.nullable().default(null),
  objectives: z.array(rangementObjectiveSchema).nullable().default(null),
  discarded: z.array(rangementDiscardSchema).nullable().default(null),
  removedExtracts: z.number().int().nonnegative().default(0),
  duplicateExtracts: rangementDuplicateExtractsSchema,
  calls: z.array(rangerCallSchema).max(RANGER_CALLS_MAX).default([]),
  costEur: z.number().nonnegative().default(0),
  startedAt: z.string().datetime(),
  settledAt: z.string().datetime().nullable().default(null),
};

/**
 * THE rangement of a session (§9): one per session at most, computed once. The
 * detail of a session carries it as `rangement`, null when its protocol had no
 * objective target (§7.2, « aucun »).
 */
export const sessionRangementSchema = z.object({
  state: rangementStateSchema,
  ...rangementFigureShape,
});
export type SessionRangement = z.infer<typeof sessionRangementSchema>;

/* ------------------------------------------------------------ the trial */

/** A trial is never `no-matter`: that case is refused before any call (`no-matter` refusal). */
export const rangementTrialStateSchema = rangementStateSchema.exclude(['no-matter']);
export type RangementTrialState = z.infer<typeof rangementTrialStateSchema>;

/**
 * A TRIAL of the ranger (§7.3): the ranger played on the conversation of a
 * session already played, with the Rangement section of the DEVELOPMENT version
 * of the protocol it played, at the moment of the press.
 *
 * It is NOT the rangement of the session, which does not move, and the
 * determination does not either. A settled trial is kept in its own table and
 * purged with the session; its cost is shown and counted nowhere else.
 *
 * - `protocolVersion` - the development version the consigne was read from.
 * - `prompts` - what the ranger was served, call by call: served by the read of
 *   ONE trial and never by the list, which carries the person's words only once.
 */
export const rangementTrialSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().min(1),
  state: rangementTrialStateSchema,
  ...rangementFigureShape,
  prompts: z.array(rangerPromptSchema).max(RANGER_CALLS_MAX).default([]),
});
export type RangementTrial = z.infer<typeof rangementTrialSchema>;

/** One trial as the LIST serves it: without what the ranger was served. */
export const rangementTrialSummarySchema = rangementTrialSchema.omit({ prompts: true });
export type RangementTrialSummary = z.infer<typeof rangementTrialSummarySchema>;

/** `GET /api/sessions/{id}/rangement/trials` - newest first: the one running, then those settled. */
export const rangementTrialListSchema = z.object({
  trials: z.array(rangementTrialSummarySchema),
});
export type RangementTrialList = z.infer<typeof rangementTrialListSchema>;

/**
 * Why a trial is refused (409), before any call (§7.3):
 *
 * - `no-protocol` - the session played no protocol that still reads;
 * - `no-matter` - the person never replied: nothing to file;
 * - `protocol-retired` - the protocol it played is retired;
 * - `rangement-missing` - the development version carries no objective target.
 */
export const rangementTrialRefusalCodeSchema = z.enum([
  'no-protocol',
  'no-matter',
  'protocol-retired',
  'rangement-missing',
]);
export type RangementTrialRefusalCode = z.infer<typeof rangementTrialRefusalCodeSchema>;

export const rangementTrialErrorSchema = z
  .object({
    error: z.string(),
    code: rangementTrialRefusalCodeSchema,
  })
  .strict();
export type RangementTrialError = z.infer<typeof rangementTrialErrorSchema>;
